import { z } from 'zod';

/**
 * Schemas Zod pra validação de forms admin.
 *
 * Mesmos schemas serão usados no backend (Frente A) pra validar payloads,
 * garantindo contrato consistente client + server.
 *
 * Mensagens em pt-BR pra erros aparecerem traduzidos no form.
 */

// ============================================================
// USUÁRIO
// ============================================================

export const userFormSchema = z.object({
  nome: z
    .string()
    .min(2, 'Nome precisa ter no mínimo 2 caracteres')
    .max(80, 'Nome máximo de 80 caracteres')
    .trim(),
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .email('Email inválido — formato esperado: nome@dominio.com')
    .toLowerCase()
    .trim(),
  isAdmin: z.boolean().default(false),
  ativo: z.boolean().default(true),
  // INDICADORES-REDESIGN (Migration 382): vendedor "de verdade" — participa de
  // metas, ranking e rateio. Só relevante pra quem tem o dept comercial.
  isVendedor: z.boolean().optional(),
  departamentos: z
    .array(
      z.object({
        // String livre — admin pode criar departamentos custom. Existência do slug é validada no UI (Select populado com departments ativos).
        departmentSlug: z.string().min(1, 'Selecione um departamento'),
        role: z.enum(['lider', 'colaborador', 'visualizador']),
      })
    )
    .default([]),
});

export type UserFormValues = z.infer<typeof userFormSchema>;

// ============================================================
// DEPARTAMENTO
// ============================================================

export const departmentFormSchema = z.object({
  nome: z
    .string()
    .min(2, 'Nome precisa ter no mínimo 2 caracteres')
    .max(50, 'Nome máximo de 50 caracteres')
    .trim(),
  slug: z
    .string()
    .min(2, 'Slug obrigatório')
    .max(30, 'Slug máximo de 30 caracteres')
    .regex(/^[a-z][a-z0-9_-]*$/, 'Slug deve conter apenas letras minúsculas, números, _ e -'),
  descricao: z.string().max(200, 'Descrição máxima de 200 caracteres').trim().default(''),
  ordem: z.number().int().min(0).max(100).default(99),
  ativo: z.boolean().default(true),
});

export type DepartmentFormValues = z.infer<typeof departmentFormSchema>;

// ============================================================
// DEAL (Comercial)
// ============================================================

/**
 * BUG-V2-055 (2026-05-25): schema HARDCODED ignorava admin config de
 * `field-requirements`. Mantido pra compat (chamadores que ainda não usam
 * o builder dinâmico), mas DealFormDialog usa `buildDealFormSchema(reqs)`
 * abaixo agora.
 *
 * Sintoma original: admin desativava obrigatoriedade de `produto` em
 * `/admin/campos-obrigatorios?workflow=comercial`, mas o form ainda
 * rejeitava submit sem produto. Causa: este schema hardcoded.
 */
export const dealFormSchema = z.object({
  titulo: z
    .string()
    .min(3, 'Título precisa ter no mínimo 3 caracteres')
    .max(120, 'Título máximo de 120 caracteres')
    .trim(),
  // FK pra clients.id (formato `client-<uuid>`). Combobox sempre passa um ID.
  // BUG-V2-001 (2026-05-18): renomeado de `cliente` pra `clienteId` pra deixar
  // intent explícito. Valor é validado no backend contra `clients(id)`.
  clienteId: z
    .string()
    .min(2, 'Selecione um cliente cadastrado')
    .max(120, 'Cliente máximo de 120 caracteres')
    .trim(),
  produto: z
    .string()
    .min(2, 'Produto é obrigatório')
    .max(80, 'Produto máximo de 80 caracteres')
    .trim(),
  servico: z
    .string()
    // trim ANTES de min/max — whitespace puro deve falhar a validação
    .trim()
    .min(2, 'Serviço é obrigatório')
    .max(120, 'Serviço máximo de 120 caracteres'),
  valor: z
    .number({ invalid_type_error: 'Valor é obrigatório' })
    .positive('Valor precisa ser maior que zero')
    .max(99_999_999, 'Valor muito alto — confira'),
  // String livre — aceita slugs canônicos DEAL_STAGES E custom criados pelo
  // admin via useKanbanColumns.addColumn (regra j do padrão Kanban, 2026-05-18).
  // Validação que o slug exista é feita no UI/business layer (Select populado
  // com columns do hook).
  fase: z.string().min(1, 'Selecione uma fase'),
  // Normalizado com service_orders em 2026-05-20 (Bloco D.1 / Migration 033).
  // Antes: ['baixa','media','alta']. Mapping: media→normal, critica→urgente.
  prioridade: z.enum(['baixa', 'normal', 'alta', 'urgente']),
  responsavelId: z.string().min(1, 'Selecione um responsável'),
  proximaAcao: z.string().max(200).trim().optional(),
  proximaAcaoDataIso: z.string().optional(),
  observacoes: z.string().max(2000, 'Observações até 2000 caracteres').trim().optional(),
  origem: z.string().max(50).trim().optional(),
  // P1 (Projetos 2.0 Parte 2): só editáveis com a flag PROJETOS_V2_ENABLED ON.
  // '' = não classificado. A regra "cliente V2 obrigatório quando 'projeto'" é
  // aplicada no DealFormDialog (depende da flag + do tipo).
  tipoVenda: z.enum(['projeto', 'recorrente']).or(z.literal('')).optional(),
  clienteV2Id: z.string().optional(),
});

export type DealFormValues = z.infer<typeof dealFormSchema>;

/**
 * BUG-V2-055 (2026-05-25): builder dinâmico do schema que respeita admin
 * config de `field-requirements`. Caller passa lookup `Map<fieldName, FieldRequirement>`
 * (do hook `useFieldRequirementsLookup('comercial', 'card')`).
 *
 * Regras:
 *   - `titulo`, `fase`, `prioridade` SEMPRE obrigatórios (intrínsecos do schema
 *     — admin não pode desligar; sem eles deal nem cria).
 *   - `clienteId`, `produto`, `servico`, `valor`, `responsavelId` → seguem
 *     `reqs.get(fieldName)?.required` (default true se config admin ausente,
 *     pra preservar comportamento histórico — usuário admin que rodou
 *     migration mas não configurou nada continua vendo "tudo obrigatório").
 *   - `proximaAcao`, `proximaAcaoDataIso`, `observacoes`, `origem` → sempre
 *     opcionais (nunca foram obrigatórios; admin não tem por que tornar).
 *
 * O builder retorna um `z.ZodObject` com `.shape` compatível com `dealFormSchema`
 * (mesmo nome de campos + mesmas chaves opcionais), então `DealFormValues`
 * type continua válido.
 */
type FieldReqLookup = Map<string, { required: boolean }>;

/**
 * Helper canônico: "este campo é obrigatório nesse workflow?".
 *
 * AUDITORIA-V2-002 / C2 (2026-05-25): default canônico fixado em FALSE
 * (opcional). Alinha 3 camadas que antes divergiam:
 *   - UI AdminConfigPage usa `?? false` (toggle OFF default) ✓
 *   - Backend assertRequiredFields só bloqueia rows com required=1 ✓
 *   - Schema dinâmico (este helper) agora também false ✓
 *
 * Por que `false` é a escolha certa:
 *   - Backend já trata ausência como "não bloqueia" → divergir aqui
 *     significava form falsamente bloqueando submit que backend aceitaria.
 *   - Com C1 (endpoint público), vendedor non-admin agora consegue ler
 *     config, então o Map raramente vai estar vazio em produção.
 *   - Loading inicial: Map vazio durante fetch → form começa permissivo,
 *     fica restritivo quando config chega. UX coerente com backend.
 *
 * Campos INTRÍNSECOS (titulo, fase, prioridade) NÃO usam este helper —
 * são hardcoded required no schema (regras de negócio invioláveis,
 * mesmas que `fixed=1` na tabela). Esta função só governa campos togláveis
 * pelo admin (produto, valor, servico, responsavelId, clienteId opcional).
 */
function isReq(reqs: FieldReqLookup | undefined, field: string, defaultVal = false): boolean {
  if (!reqs) return defaultVal;
  const entry = reqs.get(field);
  return entry?.required ?? defaultVal;
}

export function buildDealFormSchema(reqs?: FieldReqLookup) {
  return z.object({
    // Sempre obrigatório (intrínseco).
    titulo: z
      .string()
      .min(3, 'Título precisa ter no mínimo 3 caracteres')
      .max(120, 'Título máximo de 120 caracteres')
      .trim(),
    // Cliente: dinâmico
    clienteId: isReq(reqs, 'clienteId')
      ? z
          .string()
          .min(2, 'Selecione um cliente cadastrado')
          .max(120, 'Cliente máximo de 120 caracteres')
          .trim()
      : z.string().max(120).trim().optional().default(''),
    // Produto: dinâmico
    produto: isReq(reqs, 'produto')
      ? z
          .string()
          .min(2, 'Produto é obrigatório')
          .max(80, 'Produto máximo de 80 caracteres')
          .trim()
      : z.string().max(80).trim().optional().default(''),
    // Serviço: dinâmico
    servico: isReq(reqs, 'servico')
      ? z
          .string()
          .trim()
          .min(2, 'Serviço é obrigatório')
          .max(120, 'Serviço máximo de 120 caracteres')
      : z.string().trim().max(120).optional().default(''),
    // Valor: dinâmico — admin pode desligar (deal lead sem valor é caso real).
    // Quando opcional, aceita 0 ou undefined (default 0 pra preservar tipo numérico).
    valor: isReq(reqs, 'valor')
      ? z
          .number({ invalid_type_error: 'Valor é obrigatório' })
          .positive('Valor precisa ser maior que zero')
          .max(99_999_999, 'Valor muito alto — confira')
      : z
          .number({ invalid_type_error: 'Valor inválido' })
          .min(0, 'Valor não pode ser negativo')
          .max(99_999_999, 'Valor muito alto — confira')
          .optional()
          .default(0),
    // Sempre obrigatório (intrínseco — Kanban precisa de uma fase).
    fase: z.string().min(1, 'Selecione uma fase'),
    // Sempre obrigatório (intrínseco — enum fechado).
    prioridade: z.enum(['baixa', 'normal', 'alta', 'urgente']),
    // Responsável: dinâmico
    responsavelId: isReq(reqs, 'responsavelId')
      ? z.string().min(1, 'Selecione um responsável')
      : z.string().optional().default(''),
    // Sempre opcionais (não fazem sentido como required).
    proximaAcao: z.string().max(200).trim().optional(),
    proximaAcaoDataIso: z.string().optional(),
    observacoes: z.string().max(2000, 'Observações até 2000 caracteres').trim().optional(),
    origem: z.string().max(50).trim().optional(),
    // P1: idem schema estático — opcionais aqui, regra de negócio no dialog.
    tipoVenda: z.enum(['projeto', 'recorrente']).or(z.literal('')).optional(),
    clienteV2Id: z.string().optional(),
  });
}

export const dealActivitySchema = z.object({
  type: z.enum(['ligacao', 'email', 'reuniao', 'proposta', 'nota']),
  title: z.string().min(2, 'Título da atividade é obrigatório').max(120).trim(),
  description: z.string().max(2000).trim().optional(),
});

export type DealActivityFormValues = z.infer<typeof dealActivitySchema>;

// ============================================================
// CLIENT (cadastro rápido)
// ============================================================

/**
 * Valida CNPJ por formato + dígitos verificadores.
 * Aceita "00.000.000/0000-00" ou "00000000000000".
 */
function isCnpjValid(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 14) return false;
  if (/^(\d)\1+$/.test(digits)) return false; // todos iguais
  const calc = (factors: number[]) =>
    factors.reduce((acc, f, i) => acc + Number(digits[i]) * f, 0);
  const dv1Mod = calc([5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) % 11;
  const dv1 = dv1Mod < 2 ? 0 : 11 - dv1Mod;
  if (dv1 !== Number(digits[12])) return false;
  const dv2Mod = calc([6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) % 11;
  const dv2 = dv2Mod < 2 ? 0 : 11 - dv2Mod;
  return dv2 === Number(digits[13]);
}

/**
 * Schema legacy `clientFormSchema` (HARDCODED) — mantido pra compat com
 * chamadores não-migrados (tests, scripts, formulários antigos). NOVOS
 * call sites devem usar `buildClientFormSchema(reqs)` (AUDITORIA-V2-002 / C5).
 *
 * Comportamento legacy: nome+cnpj obrigatórios, email/telefone opcionais.
 */
export const clientFormSchema = z.object({
  nome: z.string().trim().min(2, 'Nome precisa ter no mínimo 2 caracteres').max(120, 'Nome até 120 caracteres'),
  cnpj: z
    .string()
    .trim()
    .min(1, 'CNPJ obrigatório')
    .refine(isCnpjValid, 'CNPJ inválido — confira os dígitos'),
  email: z.string().trim().email('Email inválido').optional().or(z.literal('')),
  telefone: z.string().trim().max(20).optional().or(z.literal('')),
});

/**
 * AUDITORIA-V2-002 / C5 (2026-05-25): builder dinâmico que respeita admin
 * config `_global/client/*` de field-requirements. Espelha
 * `buildDealFormSchema` (BUG-V2-055 fix).
 *
 * Regras:
 *   - `nome` SEMPRE obrigatório (intrínseco — `fixed=1` na migration 007;
 *     cliente sem nome é inútil). Mesmo que admin tente desligar, schema
 *     ignora — backend também rejeita (regra de negócio).
 *   - `cnpj`, `email`, `telefone` → seguem `reqs.get(fieldName)?.required`.
 *     Default canônico = false (opcional) — alinhado com AUDITORIA-V2-002/C2.
 *   - `cnpj` quando obrigatório: aplica `refine(isCnpjValid)` pra rejeitar
 *     CNPJ malformado. Quando opcional: só refine se preenchido (`literal('')` aceito).
 *
 * Type infer ainda compatível com `ClientFormValues`.
 */
export function buildClientFormSchema(reqs?: FieldReqLookup) {
  // Helper local pra cnpj com refine condicional: quando opcional + vazio
  // (ou ausente), aceita; quando preenchido, valida dígitos.
  const cnpjOptionalWithRefine = z
    .string()
    .trim()
    .max(20)
    .optional()
    .or(z.literal(''))
    .refine(
      (v) => !v || v.length === 0 || isCnpjValid(v),
      'CNPJ inválido — confira os dígitos',
    );

  return z.object({
    // Sempre obrigatório (intrínseco — fixed=1 no DB).
    nome: z
      .string()
      .trim()
      .min(2, 'Nome precisa ter no mínimo 2 caracteres')
      .max(120, 'Nome até 120 caracteres'),
    // CNPJ: dinâmico
    cnpj: isReq(reqs, 'cnpj')
      ? z
          .string()
          .trim()
          .min(1, 'CNPJ obrigatório')
          .refine(isCnpjValid, 'CNPJ inválido — confira os dígitos')
      : cnpjOptionalWithRefine,
    // Email: dinâmico (sempre validado por formato se preenchido)
    email: isReq(reqs, 'email')
      ? z.string().trim().min(1, 'Email obrigatório').email('Email inválido')
      : z.string().trim().email('Email inválido').optional().or(z.literal('')),
    // Telefone: dinâmico
    telefone: isReq(reqs, 'telefone')
      ? z.string().trim().min(1, 'Telefone obrigatório').max(20)
      : z.string().trim().max(20).optional().or(z.literal('')),
  });
}

export type ClientFormValues = z.infer<typeof clientFormSchema>;

/**
 * Gera slug a partir do nome (helper).
 *
 * "Recursos Humanos" → "recursos-humanos"
 * "Manutenção" → "manutencao"
 */
export function generateSlug(nome: string): string {
  return nome
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove acentos
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
